import DirectionsWalkRounded from '@mui/icons-material/DirectionsWalkRounded';
import SaveIcon from '@mui/icons-material/Save';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch } from '@mui/material';
import { useEffect, useState } from 'react';

import { useGetMailRoomByIdQuery } from '@api/mailroom';
import { useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';
import { type PickUpDate, PickupAction } from '@components/MailpieceActions/pick-up/PickupAction';

import { ActionComponentProps } from '../../index';
import { isPickUpLessCurrentDate } from '../../utils';
import { buildUpdatePiecesRequest } from './helper';

const AMOUNT_DAYS_OF_WEEK = 7;

export const RequestPickUp = ({ mailpieces, actionStarted, onActionFinish, isForce }: ActionComponentProps) => {
  const [updatePieces] = useUpdatePiecesMutation();
  const piecesQuantity = mailpieces.length;

  const { data: mailRoom } = useGetMailRoomByIdQuery(mailpieces[0].mailroomId, {
    skip: !piecesQuantity,
  });

  const [actionInProgress, setActionInProgress] = useState<boolean>(false);
  const [pickUpDate, setPickUpDate] = useState<PickUpDate>(null);
  const [error, setError] = useState('');
  const [shouldNotify, setShouldNotify] = useState(true);

  useEffect(() => {
    if (actionStarted) {
      const activeSpans = mailRoom?.hoursOfOperation.activeSpans || [];
      const date = new Date();
      if (activeSpans.length) {
        const sortedSpans = [...activeSpans].sort((spanA, spanB) => spanA.dayOfWeek - spanB.dayOfWeek);

        const activeSpan = sortedSpans.find((span) => span.dayOfWeek >= date.getDay()) || activeSpans[0];

        const daysToClosestSpan =
          activeSpan.dayOfWeek >= date.getDay()
            ? activeSpan.dayOfWeek - date.getDay()
            : AMOUNT_DAYS_OF_WEEK - date.getDay() + activeSpan.dayOfWeek;

        date.setDate(date.getDate() + daysToClosestSpan);
      }

      setPickUpDate(date);
      setError('');
      setShouldNotify(true);
    }
  }, [actionStarted, mailRoom]);

  const onChangeValue = (date: PickUpDate) => setPickUpDate(date);
  const onChangeError = setError;

  const submit = async () => {
    if (error || !pickUpDate) return;

    if (isPickUpLessCurrentDate(pickUpDate)) {
      setError('This time is not available.');
      return;
    }

    try {
      setActionInProgress(true);

      const UTCPickUpDate = pickUpDate.toISOString();

      const requestData = buildUpdatePiecesRequest({
        mailpieces,
        action: PieceAction.RequestPickUp,
        isForce,
        shouldNotify,
        additionalData: {
          pickupDate: UTCPickUpDate,
        },
      });

      await updatePieces(requestData).unwrap();

      setActionInProgress(false);
      onActionFinish();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Dialog slotProps={{ paper: { sx: { width: 450 } } }} open={actionStarted}>
      <DialogTitle>
        {piecesQuantity && piecesQuantity > 1 ? 'Request pickup for selected items' : 'Request pickup'}
      </DialogTitle>
      <DialogContent>
        <PickupAction
          mailRoom={mailRoom}
          pickUpDate={pickUpDate}
          onChangeValue={onChangeValue}
          error={error}
          onChangeError={onChangeError}
          piecesQuantity={piecesQuantity}
        />

        {isForce && (
          <FormControlLabel
            control={<Switch />}
            sx={{ mt: 3 }}
            checked={shouldNotify}
            onChange={() => setShouldNotify(!shouldNotify)}
            label="Notify Mailroom"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onActionFinish} color="inherit" disabled={actionInProgress}>
          Cancel
        </Button>
        <Button
          onClick={submit}
          loading={actionInProgress}
          startIcon={actionInProgress && <SaveIcon />}
          loadingPosition={actionInProgress ? 'start' : undefined}
          variant="contained"
          color="primary"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const requestPickUpConfig = {
  title: 'Pick Up',
  Icon: DirectionsWalkRounded,
};
